/* eslint-disable max-lines-per-function */
import React, { useCallback, useState } from 'react';

import { Badge } from '@change/ds-core-ui/Badge';
import { Text } from '@change/ds-core-ui/Text';
import { FocusRing } from '@change/ds-core-ui/util';
import { ArrowRightIcon } from '@change/ds-icons/ArrowRightIcon';
import { TrendingIcon } from '@change/ds-icons/TrendingIcon';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { useScrollToSection } from '#src/app/pages/petitionDetails/shared/hooks';
import { focus } from '#src/app/shared/utils/dom';

import type { SpotlightContent } from '../../hooks/useSpotlightCardContent/types';
import { useMomentumBarTracking } from '../../hooks/useTracking';

// eslint-disable-next-line complexity
export const SpotlightCard: React.FC<SpotlightCardProps> = ({ items, testId, qaId }) => {
	const [currentIndex, setCurrentIndex] = useState(0);
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePetitionDetailsPageData();
	// the first item is the most prominent one, so start over when the petition or the item count changes
	const resetKey = `${petitionId}:${items.length}`;
	const [prevResetKey, setPrevResetKey] = useState(resetKey);
	if (prevResetKey !== resetKey) {
		setPrevResetKey(resetKey);
		setCurrentIndex(0);
	}
	const {
		actions: { scrollToSection },
	} = useScrollToSection();
	const tracking = useMomentumBarTracking();
	const { translate } = useI18n();

	const safeIndex = items.length > 0 ? currentIndex % items.length : 0;
	const currentItem = items[safeIndex];
	const canAdvance = items.length > 1;
	const sectionId = currentItem?.sectionId;

	const handleCardClick = useCallback(() => {
		if (sectionId) {
			tracking.trackSpotlightContentClick(petitionId);
			scrollToSection(sectionId);
			focus(document.querySelector<HTMLElement>(`#${sectionId}`), { makeFocusable: true, preventScroll: true });
		}
	}, [petitionId, scrollToSection, sectionId, tracking]);

	const advance = () => {
		tracking.trackSpotlightNavigationClick(petitionId);
		setCurrentIndex((i) => (i + 1) % items.length);
	};

	if (!currentItem) return null;

	return (
		<>
			<FocusRing disabled={!canAdvance}>
				{/* oxlint-disable-next-line jsx-a11y/no-static-element-interactions -- not fixed to avoid regression risk from an automated markup rewrite; needs manual accessibility review */}
				<div
					data-testid={testId}
					data-qa={qaId}
					className={cn(
						'border-stroke-weaker dark:bg-background-raised bg-gradient-background-weak-3 mb-2.5 flex w-full flex-col items-start gap-3 rounded-md border pt-3.5 pr-3.5 pb-3.5 pl-3.5 shadow-[0_2px_2px_rgba(0,0,0,0.05),0_3px_19px_rgba(58,41,62,0.06)] lg:mb-0 lg:w-[60%] lg:gap-6 lg:pt-6 lg:pr-5 lg:pl-5 dark:bg-none',
						canAdvance && 'cursor-pointer',
					)}
					onClick={canAdvance ? advance : undefined}
					role={canAdvance ? 'button' : undefined}
					tabIndex={canAdvance ? 0 : undefined}
					aria-label={canAdvance ? translate('corgi.petition_details.momentum_bar.next_activity') : undefined}
					onKeyDown={
						canAdvance
							? (e: React.KeyboardEvent) => {
									if (e.target !== e.currentTarget) return;
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										advance();
									}
								}
							: undefined
					}
				>
					{/* Top row: badge + navigation pill (pill only shown when there are multiple items) */}
					<div className="flex flex-row items-center gap-2">
						<div key={safeIndex} className="animate-spotlight-card-enter shrink-0 motion-reduce:animate-none">
							<Badge
								emphasis="strong"
								label={currentItem.badgeLabel}
								size="tiny"
								variant="neutral"
								data-qa={qaId ? `${qaId}-badge` : undefined}
							/>
						</div>
						{canAdvance && (
							<FocusRing>
								<button
									type="button"
									onClick={(e) => {
										e.stopPropagation();
										advance();
									}}
									aria-label={translate('corgi.petition_details.momentum_bar.spotlight_pagination_next')}
									data-qa={qaId ? `${qaId}-counter` : undefined}
									className={cn(
										'flex h-5 items-center gap-0.5 rounded-full',
										'border-stroke-weaker border px-2.5 py-2',
										'text-icon-weak text-xs leading-5 font-light',
									)}
								>
									<Text variant="body" size="x-small" className="tabular-nums">
										{safeIndex + 1}/{items.length}
									</Text>
									<ArrowRightIcon size="x-small" />
								</button>
							</FocusRing>
						)}
					</div>
					<div key={`text-${safeIndex}`} className="animate-spotlight-card-enter w-full motion-reduce:animate-none">
						{sectionId ? (
							<button
								type="button"
								className="text-icon-strong focus-visible:ring-icon-strong mb-0 flex w-full cursor-pointer items-center gap-2 rounded border-0 bg-transparent p-0 text-left font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 lg:mb-3.5"
								onClick={(e) => {
									e.stopPropagation();
									handleCardClick();
								}}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										e.stopPropagation();
										handleCardClick();
									}
								}}
								aria-label={translate('corgi.petition_details.momentum_bar.go_to_section', {
									section: currentItem.badgeLabel,
								})}
								data-qa={qaId ? `${qaId}-label` : undefined}
							>
								<Text
									variant="body"
									size="large"
									className="text-icon-strong mb-0 flex items-center gap-2 font-semibold"
								>
									{currentItem.showIcon ? <TrendingIcon size="small" /> : null}
									{currentItem.relativeTime !== undefined ? (
										<span className="min-w-0 text-left hover:underline">
											<span>{currentItem.text}</span>{' '}
											<span className="ml-1 font-light">{currentItem.relativeTime}</span>
										</span>
									) : (
										<span className="hover:underline">{currentItem.text}</span>
									)}
								</Text>
							</button>
						) : (
							<Text
								variant="body"
								size="large"
								className="text-icon-strong mb-0 flex items-center gap-2 font-semibold lg:mb-3.5"
								data-qa={qaId ? `${qaId}-label` : undefined}
							>
								{currentItem.showIcon ? <TrendingIcon size="small" /> : null}
								{currentItem.text}
							</Text>
						)}
					</div>
				</div>
			</FocusRing>
			{/* outside the role="button" card, because a button's children are presentational and not announced */}
			{canAdvance && (
				<span aria-live="polite" className="sr-only" data-qa={qaId ? `${qaId}-announcement` : undefined}>
					{currentItem.badgeLabel} {currentItem.text}
				</span>
			)}
		</>
	);
};

export type SpotlightCardProps = {
	items: readonly SpotlightContent[];
	testId?: string;
	qaId?: string;
};
